import type { ReactNode } from 'react';
import { bankLockedLabel, bankLockedOriginNote } from '../../utils/bankLockedLabel';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import type { TFunction } from 'i18next';
import { format } from 'date-fns';
import { ExternalLink, Pencil, Square, Timer, Trash2 } from 'lucide-react';
import InlineTimeRange from './InlineTimeRange';
import type { TicketStatus, WorkLog, WorkRecord } from '../../types';
import { getStatusLabel } from '../../types';
import Badge from '../ui/Badge';
import { statusTone } from '../ui/tone';
import VoiceAiBadge from '../VoiceAiBadge';
import { formatDate, formatDateTime } from '../../utils/format';
import type { CardSpec, Column, RowAction } from '../DataTable';
import { durationParts, isPendingReview } from './workLogsListParams';
import type { WorkLogType } from './workLogsFiltersModel';
import { summaryDisplay } from './runningSummary';
import { taskContainerLabel } from '../../utils/engagementLabel';
import { OrgName } from '../OrgLink';
import ClaudeBadge from '../claude/ClaudeBadge';
import { isClaudeMarked } from '../claude/useClaudeMarks';

/**
 * Work logs on DataTable v2 (T-40, PRD §4.10): the columns, the phone card and
 * the row actions of the two views ("רישומים" and "מחזורי עבודה"), plus the
 * running timer as a pinned row. Compact single-line rows (Q-018b); the item is the row
 * link (the ticket / task); approve and edit appear on row hover /
 * focus, delete is in the row "⋯" (D5); on touch all three are in the "⋯" sheet.
 *
 * The task is the unit of work (project → milestone → task): a row's title,
 * link and status are the task's; its milestone is a secondary marker.
 */

/** "45 דק׳" below an hour, "2:05 שע׳" from an hour (DESIGN_SYSTEM.md §11.8). */
export function formatDuration(minutes: number, t: TFunction): string {
  const { hours, minutes: m, padded } = durationParts(minutes);
  return hours > 0 ? t('workLogs.list.durationHours', { h: hours, mm: padded }) : t('workLogs.list.durationMinutes', { m });
}

/** C-13 (clarity, A-10): a summary number is always hours — "3:20 שע׳", never "200 דק׳". */
export function formatHoursClock(minutes: number, t: TFunction): string {
  const { hours, padded } = durationParts(minutes);
  return t('workLogs.list.durationHours', { h: hours, mm: padded });
}

const hm = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'HH:mm');
};

/** "23/09/2026 · 17:20–17:22" (the end only when it is the same day). */
export function whenLabel(log: Pick<WorkLog, 'start_time' | 'end_time'>): string {
  if (!log.start_time) return '';
  const start = new Date(log.start_time);
  if (Number.isNaN(start.getTime())) return '';
  const end = log.end_time ? new Date(log.end_time) : null;
  const sameDay = !!end && !Number.isNaN(end.getTime()) && end.toDateString() === start.toDateString();
  if (end && !sameDay) return `${formatDateTime(log.start_time)} – ${formatDateTime(log.end_time)}`;
  return `${formatDate(log.start_time)} · ${hm(log.start_time)}${end ? `–${hm(log.end_time)}` : ''}`;
}

/** The row title: the task / ticket the time is on (a milestone is never the item). */
export function itemTitle(log: WorkLog, t: TFunction): string {
  return log.ticket_title || t('workLogs.noTitle');
}

/** Where the row goes: the task / ticket. */
export function workLogHref(log: WorkLog): string {
  return `/tickets/${log.ticket_id}?from=/work-logs`;
}

function typeLabel(log: Pick<WorkLog, 'task_type'>, t: TFunction): string {
  return log.task_type === 'task' ? t('workLogs.task') : t('workLogs.ticket');
}

/**
 * After the item: "משימה" for a task, the milestone the task belongs to (a
 * secondary link), and the voice-AI source.
 */
function ItemMarkers({ log, t }: { log: WorkLog; t: TFunction }) {
  return (
    <>
      {log.task_type === 'task' && <span className="shrink-0 text-xs text-text-3">{t('workLogs.task')}</span>}
      {log.milestone_id && log.milestone_name && (
        <span className="flex min-w-0 max-w-56 items-center gap-1 text-xs text-text-3">
          <span className="shrink-0">{t('workLogs.milestone')}</span>
          <span className="text-text-disabled" aria-hidden="true">·</span>
          <Link
            to={`/milestones/${log.milestone_id}?from=/work-logs`}
            className="min-w-0 truncate rounded-xs hover:text-text hover:underline"
            title={log.milestone_name}
          >
            <bdi>{log.milestone_name}</bdi>
          </Link>
        </span>
      )}
      {log.source === 'voice_ai' && <VoiceAiBadge confidence={log.ai_confidence} />}
    </>
  );
}

/**
 * A work-log / segment summary. The backend placeholder "(running)" is not
 * user text: it renders as a quiet hint ("טיימר פעיל" while the segment is
 * open, "מקטע טיימר ללא סיכום" once it closed), never as the summary itself.
 */
export function WorkLogSummary({ summary, ended, t, className = 'text-xs text-text-2', empty = null }: {
  summary: string | null | undefined;
  /** False only for a segment that is still running (no end time). */
  ended?: boolean;
  t: TFunction;
  className?: string;
  /** What to show when there is no summary at all. */
  empty?: ReactNode;
}) {
  const d = summaryDisplay(summary, { ended });
  if (d.kind === 'text') return <span className={className} title={d.text}><bdi>{d.text}</bdi></span>;
  if (d.kind === 'empty') return <>{empty}</>;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-text-3">
      {d.kind === 'running'
        ? <span className="live-dot" aria-hidden="true" />
        : <Timer size={12} strokeWidth={1.75} aria-hidden="true" />}
      {d.kind === 'running' ? t('form.summaryRunning') : t('form.summaryTimerNoSummary')}
    </span>
  );
}

/** The review state: pending (warning) or approved (success); nothing when the log needs no review. */
function ReviewBadge({ log, t }: { log: WorkLog; t: TFunction }) {
  if (isPendingReview(log)) return <Badge tone="warning" dot>{t('workLogs.pendingReview')}</Badge>;
  if (log.requires_review && log.reviewed) return <Badge tone="success" dot>{t('workLogs.reviewed')}</Badge>;
  return null;
}

/** D-287: an entry shows the over-quota / locked tag when it waits (pending) or carries locked bank hours. */
function showsOverQuota(log: Pick<WorkLog, 'over_quota_status' | 'over_quota_locked_minutes'>): boolean {
  return log.over_quota_status === 'pending' || (log.over_quota_locked_minutes ?? 0) > 0;
}

/** D-266 sec.1: hours beyond the quota ("מעבר למכסה — ממתין לאישור") / the bank ("נעולות"); nothing otherwise. */
export function OverQuotaBadge({ log, t }: { log: Pick<WorkLog, 'over_quota_status' | 'over_quota_kind' | 'over_quota_locked_minutes' | 'over_quota_total_minutes' | 'over_quota_origin_date'>; t: TFunction }) {
  const lockedBank = (log.over_quota_locked_minutes ?? 0) > 0;
  if (log.over_quota_status !== 'pending' && !lockedBank) return null;
  if (!lockedBank && log.over_quota_kind !== 'hour_bank') return <Badge tone="warning" dot>{t('workLogs.overQuotaPending')}</Badge>;
  // D-287: the tag sits on the entries that will really move; a pending log with nothing locked in it gets none.
  if (log.over_quota_locked_minutes === 0) return null;
  const note = bankLockedOriginNote(t as never, log.over_quota_origin_date);
  return (
    <>
      <Badge tone="warning" dot>{bankLockedLabel(t as never, log.over_quota_locked_minutes, log.over_quota_total_minutes)}</Badge>
      {note && <span className="ms-1 text-xs text-text-3">{note}</span>}
    </>
  );
}

function StatusCell({ log, t }: { log: WorkLog; t: TFunction }) {
  const review = <ReviewBadge log={log} t={t} />;
  const nonBillable = !log.billable;
  const overQuota = showsOverQuota(log);
  // The usual case (billable, no review needed) stays quiet: only exceptions show.
  if (!isPendingReview(log) && !(log.requires_review && log.reviewed) && !nonBillable && !overQuota) return null;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      {review}
      <OverQuotaBadge log={log} t={t} />
      {nonBillable && <span className="text-xs text-text-2">{t('common.nonBillable')}</span>}
    </span>
  );
}

/** The task's (or ticket's) status — the item of the row, not its milestone. */
function itemStatusBadge(log: WorkLog, t: TFunction): ReactNode {
  if (log.ticket_status) return <Badge tone={statusTone(log.ticket_status)} dot>{getStatusLabel(log.ticket_status as TicketStatus, t)}</Badge>;
  return null;
}

interface WorkLogColumnOptions {
  t: TFunction;
  type: WorkLogType;
  /** Internal roles see when the work happened (customers' rows carry no times). */
  showTime: boolean;
  /** The running timer, when it is pinned above the rows (`timerLog`). */
  timer?: TimerRowInfo | null;
  /** D-159 — false for a worker who sees only their own logs: no "עובד" column. */
  showWorker?: boolean;
  /** D-292 - the start / end time in the row is edited in place (the same gate as the edit button). */
  canEditTimes?: boolean;
  /**
   * DQ-043 - may the caller change THIS log's times / delete it? The server allows that only on
   * one's own logs (the OTE manager: everyone's); a lead who sees others' logs may only flip
   * "לחיוב" (D-71, D-92). Unset = every row (as before).
   */
  canChangeLog?: (log: WorkLog) => boolean;
  /** FIX-CLAUDE-B (D-314 §11): logs made through Claude (the "Claude" tag, OTE employees only). */
  claudeMarks?: Set<string>;
}

export function workLogColumns({ t, type, showTime, timer, showWorker = true, canEditTimes = false, canChangeLog, claudeMarks }: WorkLogColumnOptions): Column<WorkLog>[] {
  const cols: Column<WorkLog>[] = [
    {
      key: 'item',
      header: t('workLogs.list.colItem'),
      link: true,
      render: (log) => (isTimerLog(log) ? <TimerTitle title={itemTitle(log, t)} /> : <bdi>{itemTitle(log, t)}</bdi>),
      trail: (log) => (isTimerLog(log) ? null : (
        <>
          <ItemMarkers log={log} t={t} />
          {claudeMarks && isClaudeMarked(claudeMarks, log.id) && <ClaudeBadge />}
        </>
      )),
    },
    {
      key: 'customer',
      header: t('workLogs.customerColumn'),
      priority: 2,
      width: 'w-40',
      render: (log) => (isTimerLog(log) ? null : (
        // QA-3 #7 — the customer (organization), not who opened the task.
        <span className="block max-w-40 truncate text-xs text-text-2">
          {log.organization_name ? <OrgName orgId={log.organization_id} name={log.organization_name} /> : t('common.notSpecified')}
        </span>
      )),
    },
    ...(showWorker ? [{
      key: 'worker',
      header: t('workLogs.workerColumn'),
      priority: 2,
      width: 'w-32',
      render: (log: WorkLog) => <span className="block max-w-32 truncate text-xs text-text-2" title={log.agent_name}>{log.agent_name}</span>,
    }] as Column<WorkLog>[] : []),
    {
      key: 'summary',
      header: t('ticketDetail.summary'),
      priority: 3,
      card: 'details',
      render: (log) => (
        <WorkLogSummary summary={log.summary} ended={!!log.end_time} t={t} className="block max-w-56 truncate text-xs text-text-2" />
      ),
    },
  ];
  if (type === 'tasks' || type === 'milestones') {
    cols.push({
      key: 'itemStatus',
      header: t('workLogs.list.colTaskStatus'),
      priority: 3,
      width: 'w-32',
      card: 'badges',
      render: (log) => (isTimerLog(log) ? null : itemStatusBadge(log, t)),
    });
  }
  if (showTime) {
    cols.push({
      key: 'when',
      header: t('workLogs.list.colWhen'),
      align: 'end',
      width: 'w-48',
      render: (log) => (isTimerLog(log)
        ? (
          <span className="whitespace-nowrap text-xs tabular-nums">
            <span className="text-accent-soft-text">{t('workLogs.timerInProgress')}</span>
            {timer?.startedAt && <span className="text-text-2"> · {t('workLogs.list.timerSince', { time: hm(timer.startedAt) })}</span>}
          </span>
        )
        : canEditTimes && log.start_time && (!canChangeLog || canChangeLog(log))
          ? <InlineTimeRange log={log} canEdit className="text-xs text-text-2" />
          : <bdi dir="ltr" className="whitespace-nowrap text-xs text-text-2">{whenLabel(log) || '—'}</bdi>),
    });
  }
  cols.push(
    {
      key: 'duration',
      header: t('uxLists.durationHeader'),
      align: 'end',
      width: 'w-24',
      card: 'end',
      render: (log) => (isTimerLog(log)
        ? null
        : <span className="whitespace-nowrap font-medium text-text">{formatDuration(log.duration_minutes, t)}</span>),
    },
  );
  // D-159 / D-72: no "בדיקה" column — there are no approvals on this page. Only a
  // pinned running timer keeps its one action (the stop button) in that place.
  if (timer) {
    cols.push({
      key: 'review',
      header: t('workLogs.list.colReview'),
      width: 'w-36',
      card: 'badges',
      render: (log) => (isTimerLog(log) ? <StopButton timer={timer} t={t} /> : <StatusCell log={log} t={t} />),
    });
  }
  return cols;
}

/** Phone card: item + duration · review / billable · when · customer · worker. */
export function workLogCard(log: WorkLog, t: TFunction, showTime: boolean, showWorker = true): CardSpec {
  const meta: ReactNode[] = [
    showTime && whenLabel(log) ? <bdi dir="ltr" className="whitespace-nowrap">{whenLabel(log)}</bdi> : null,
    log.organization_name,
    showWorker ? log.agent_name : null,
  ];
  const pending = isPendingReview(log);
  const badges = (
    <>
      <ReviewBadge log={log} t={t} />
      <OverQuotaBadge log={log} t={t} />
      {!log.billable && <Badge tone="neutral">{t('common.nonBillable')}</Badge>}
      {log.source === 'voice_ai' && <VoiceAiBadge confidence={log.ai_confidence} />}
    </>
  );
  return {
    title: <bdi>{itemTitle(log, t)}</bdi>,
    end: <span className="font-medium text-text">{formatDuration(log.duration_minutes, t)}</span>,
    badges: pending || (log.requires_review && log.reviewed) || !log.billable || log.source === 'voice_ai' || showsOverQuota(log) ? badges : undefined,
    metaItems: meta,
    details: [
      { label: t('workLogs.filter.type'), value: typeLabel(log, t) },
      ...(log.milestone_id && log.milestone_name ? [{ label: t('workLogs.milestone'), value: <bdi>{log.milestone_name}</bdi> }] : []),
      ...(log.summary ? [{ label: t('ticketDetail.summary'), value: <WorkLogSummary summary={log.summary} ended={!!log.end_time} t={t} className="" /> }] : []),
    ],
  };
}

export function workLogRowActions(
  log: WorkLog,
  opts: { t: TFunction; canEdit: boolean; onEdit: () => void; onDelete: () => void; /** DQ-043 - false: someone else's log (only "לחיוב" may change, no delete). */ ownOrManager?: boolean },
): RowAction[] {
  const { t } = opts;
  const actions: RowAction[] = [];
  if (opts.canEdit) {
    // P-10c (D-76): an approved service-contract month is locked (the server
    // answers 409) until an OTE manager re-opens it.
    const locked = !!log.service_log_locked;
    const lockedHint = locked ? t('workLogs.list.serviceLogLocked') : undefined;
    actions.push(
      { key: 'edit', label: t('workLogs.list.edit'), icon: <Pencil size={16} strokeWidth={1.75} />, inline: true, disabled: locked, hint: lockedHint, onSelect: opts.onEdit },
    );
    // DQ-043 - someone else's log: the server refuses the delete (403), so it is not offered.
    if (opts.ownOrManager !== false) actions.push(
      {
        key: 'delete',
        label: t('workLogs.list.delete'),
        icon: <Trash2 size={16} strokeWidth={1.75} />,
        danger: true,
        separatorBefore: true,
        disabled: locked,
        hint: lockedHint,
        onSelect: opts.onDelete,
      }
    );
  }
  return actions;
}

/* ------------------------------------------------------------------ */
/* Running timer: a pinned live row at the top of the table            */
/* ------------------------------------------------------------------ */

export interface TimerRowInfo {
  ticketId: string;
  title: string;
  taskType?: string | null;
  startedAt?: string | null;
  userName?: string;
  onStop: () => void;
}

/** Row id of the running timer (never a real work-log id). */
export const TIMER_ROW_ID = '__running-timer';

export const isTimerLog = (log: Pick<WorkLog, 'id'>) => log.id === TIMER_ROW_ID;

/**
 * The running timer as a pinned DataTable row (`pinnedTop`): it is not a work
 * log yet, so it is not selectable and carries no review state; it links to
 * its ticket / task and its one action is "עצירה".
 */
export function timerLog(timer: TimerRowInfo): WorkLog {
  return {
    id: TIMER_ROW_ID,
    ticket_id: timer.ticketId,
    agent_id: '',
    source: 'timer',
    start_time: timer.startedAt ?? '',
    end_time: '',
    duration_minutes: 0,
    summary: '',
    requires_review: false,
    reviewed: false,
    billable: true,
    created_at: timer.startedAt ?? '',
    updated_at: timer.startedAt ?? '',
    ticket_title: timer.title,
    task_type: timer.taskType ?? undefined,
    agent_name: timer.userName,
  };
}

/** The live row's ground: solid accent-soft (DESIGN_SYSTEM.md §2.8 live state). */
export const TIMER_ROW_CLASS = 'bg-accent-soft hover:bg-accent-soft';

function TimerTitle({ title }: { title: string }) {
  return (
    <>
      {/* Live edge on the row's start side (the row is the containing block). */}
      <span className="absolute inset-y-0 start-0 w-0.5 bg-live" aria-hidden="true" />
      <span className="status-dot-active me-2 inline-block align-middle" aria-hidden="true" />
      <bdi>{title}</bdi>
    </>
  );
}

function TimerMarker({ timer, t }: { timer?: TimerRowInfo | null; t: TFunction }) {
  return (
    <span className="whitespace-nowrap text-xs text-accent-soft-text">
      {timer?.taskType === 'task' ? t('workLogs.task') : t('workLogs.ticket')} · {t('workLogs.timerInProgress')}
    </span>
  );
}

function StopButton({ timer, t }: { timer: TimerRowInfo; t: TFunction }) {
  return (
    <button
      type="button"
      onClick={timer.onStop}
      aria-label={`${t('timer.stop')}: ${timer.title}`}
      className="dt-above btn btn-secondary btn-sm"
    >
      <Square className="h-3.5 w-3.5" aria-hidden="true" />
      {t('timer.stop')}
    </button>
  );
}

/** Phone card of the running timer: title · live state · since · stop. */
export function timerCard(timer: TimerRowInfo, t: TFunction): CardSpec {
  return {
    title: <><span className="status-dot-active me-2 inline-block align-middle" aria-hidden="true" /><bdi>{timer.title}</bdi></>,
    end: <StopButton timer={timer} t={t} />,
    badges: <TimerMarker timer={timer} t={t} />,
    metaItems: [timer.startedAt ? <span className="tabular-nums">{t('workLogs.list.timerSince', { time: hm(timer.startedAt) })}</span> : null, timer.userName],
  };
}

/* ------------------------------------------------------------------ */
/* Work cycles ("מחזורי עבודה")                                        */
/* ------------------------------------------------------------------ */

/**
 * Work cycles: the row expands (DataTable `renderExpanded`) to show the
 * cycle's summary and time segments; the whole row is the disclosure.
 */
export function workRecordColumns({ t, showWorker = true, claudeMarks }: { t: TFunction; showWorker?: boolean; claudeMarks?: Set<string> }): Column<WorkRecord>[] {
  return [
    {
      key: 'item',
      header: t('workLogs.list.colItem'),
      render: (rec) => (
        <span className="block truncate font-medium text-text" title={rec.ticket_title || undefined}>
          <bdi>{rec.ticket_title || t('workLogs.noTitle')}</bdi>
          {/* FIX-CLAUDE-B (D-314 §11) */}
          {claudeMarks && isClaudeMarked(claudeMarks, rec.id) && <ClaudeBadge className="ms-2" />}
        </span>
      ),
      className: 'max-w-0 w-full',
    },
    ...(showWorker ? [{
      key: 'worker',
      header: t('records.worker'),
      width: 'w-36',
      card: 'meta',
      render: (rec: WorkRecord) => <span className="block max-w-36 truncate text-xs text-text-2">{rec.agent_name || t('common.notSpecified')}</span>,
    }] as Column<WorkRecord>[] : []),
    {
      key: 'project',
      header: t('records.project'),
      priority: 3,
      width: 'w-48',
      card: 'meta',
      render: (rec) => {
        // P-02b: a task with no project shows its engagement ("name · type").
        const text = [rec.milestone_name, taskContainerLabel(rec, t)].filter(Boolean).join(' · ');
        // T-56 (D-188): the company reads "OTE, עבודה פנימית".
        const org = rec.organization_name ? <OrgName orgId={rec.organization_id} name={rec.organization_name} /> : null;
        return text || org
          ? <span className="block max-w-48 truncate text-xs text-text-2">{text}{text && org ? ' · ' : ''}{org}</span>
          : null;
      },
    },
    {
      key: 'status',
      header: t('records.status'),
      width: 'w-36',
      card: 'badges',
      render: (rec) => (
        <span className="inline-flex items-center gap-2 whitespace-nowrap">
          <Badge tone={rec.status === 'open' ? 'info' : 'neutral'} dot>
            {rec.status === 'open' ? t('records.statusOpen') : t('records.statusClosed')}
          </Badge>
          {!rec.billable && <span className="text-xs text-text-2">{t('common.nonBillable')}</span>}
        </span>
      ),
    },
    {
      key: 'segments',
      header: t('records.segmentCount'),
      align: 'end',
      priority: 2,
      width: 'w-20',
      card: 'details',
      render: (rec) => <span className="text-xs text-text-2">{typeof rec.segments_count === 'number' ? rec.segments_count : '—'}</span>,
    },
    {
      key: 'hours',
      header: t('records.totalHours'),
      align: 'end',
      width: 'w-24',
      card: 'end',
      render: (rec) => <span className="whitespace-nowrap font-medium text-text">{formatDuration(rec.total_minutes ?? 0, t)}</span>,
    },
  ];
}

/** Phone card for a work cycle: item + hours · status · worker · project / customer. */
export function workRecordCard(rec: WorkRecord, t: TFunction, showWorker = true): CardSpec {
  const meta = [showWorker ? rec.agent_name : null, rec.milestone_name,
    taskContainerLabel(rec, t) || (rec.organization_name ? <OrgName orgId={rec.organization_id} name={rec.organization_name} /> : null)].filter(Boolean) as ReactNode[];
  return {
    title: rec.ticket_title || t('workLogs.noTitle'),
    end: <span className="font-medium text-text">{formatDuration(rec.total_minutes ?? 0, t)}</span>,
    badges: (
      <>
        <Badge tone={rec.status === 'open' ? 'info' : 'neutral'} dot>{rec.status === 'open' ? t('records.statusOpen') : t('records.statusClosed')}</Badge>
        {!rec.billable && <Badge tone="neutral">{t('common.nonBillable')}</Badge>}
      </>
    ),
    metaItems: meta,
  };
}

export function workRecordRowActions(rec: WorkRecord, opts: { t: TFunction; onOpenTicket: () => void }): RowAction[] {
  const { t } = opts;
  return [
    {
      key: 'ticket',
      label: rec.task_type === 'task' ? t('workLogs.cycles.openTask') : t('workLogs.cycles.openTicket'),
      icon: <ExternalLink size={16} strokeWidth={1.75} />,
      onSelect: opts.onOpenTicket,
    },
  ];
}
